// vista#631: per-device "watch" toggle. A watched device is probed at a
// faster ICMP cadence by its proxy (the agent merges it into the proxy's
// monitoring profiles). Renders a star button; filled = watched.
//
// Mirrors DeviceAliasEditor's PATCH + cache-invalidation pattern so the
// toggle behaves the same wherever a device is shown (Devices row,
// DeviceDetail). Surfaces the agent's per-proxy cap (HTTP 409) inline.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

export default function WatchToggle({ device, agentId, withLabel = false, className = '' }) {
  const queryClient = useQueryClient();
  const watched = !!device.watched;

  const mutation = useMutation({
    mutationFn: (next) =>
      api.patch(`/devices/${device.device_id}`, { watched: next }, { agent_id: agentId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device'] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  // Surface ANY failure, not just the 409 cap (#642): a wrong/missing
  // agent_id used to 400/404 and show nothing, so the toggle looked dead.
  // 409 (cap) and other errors both carry a body.error from the agent;
  // fall back to the transport message.
  const errMsg = mutation.error
    ? mutation.error.body?.error || mutation.error.message || 'Failed to update watch'
    : null;
  const title = errMsg
    ? errMsg
    : watched
      ? 'Watching — faster monitoring. Click to stop.'
      : 'Add to watch list (faster monitoring)';

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <button
        type="button"
        aria-pressed={watched}
        aria-label={watched ? 'Stop watching device' : 'Watch device'}
        title={title}
        disabled={mutation.isPending}
        onClick={(e) => {
          // Stop row-level navigation when embedded in a clickable table row.
          e.stopPropagation();
          e.preventDefault();
          mutation.mutate(!watched);
        }}
        className={`rounded p-1 transition-colors disabled:opacity-50 ${
          watched
            ? 'text-amber-500 hover:bg-amber-50'
            : 'text-gray-300 hover:bg-gray-100 hover:text-gray-500'
        }`}
      >
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill={watched ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      </button>
      {withLabel && (
        <span className={`text-xs ${watched ? 'text-amber-600' : 'text-gray-500'}`}>
          {watched ? 'Watching' : 'Watch'}
        </span>
      )}
      {errMsg && <span className="text-xs text-red-600">{errMsg}</span>}
    </span>
  );
}
